import { useState } from 'react'
import { Button, Flex, Typography } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { addMoney, formatMoneyValue, negateMoney, type Money } from '@/shared/lib/money'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { CashFeedbackAlert } from './CashFeedbackAlert'
import { ConfirmAction } from './ConfirmAction'
import { fundSubscriber, type DirectoryRow } from './cashdeskApi'
import { useCashOperation } from './useCashOperation'

interface FundPanelProps {
  subscriber: DirectoryRow
  agentId: string
  /** Kassir hozir sarflay oladigan summa (depozit + qarz limiti) */
  available: Money | null
  /**
   * Bitta to'lovning shifti; `null` — chegara yo'q yoki noma'lum.
   *
   * ⚠️ «Depozitdan ko'p» dan BOSHQA holat: depozit yetmasligi mumkin,
   * lekin shift oshib ketishi serverning QAT'IY rad javobi — u qarz
   * limiti bilan ham ochilmaydi. Shuning uchun bu tugmani O'CHIRADI,
   * ogohlantirish bilan cheklanmaydi.
   */
  perTxnLimit: Money | null
  /**
   * Tez tanlanadigan summalar.
   *
   * ⚠️ SONLAR O'YLAB TOPILMAYDI. «10 000 / 20 000 / 50 000» kabi
   * tugmalar so'm uchun mantiqiy, afg'oniyda esa ma'nosiz (bir oylik
   * tarif ikki xonali son bo'lishi mumkin). Shuning uchun taklif
   * abonentning O'Z tarifi narxidan olinadi — narx noma'lum bo'lsa
   * ro'yxat bo'sh qoladi va faqat qo'lda kiritish qoladi.
   */
  suggestions: readonly Money[]
  currency: string
  onBack: () => void
}

/**
 * Abonent balansini kassirning depozitidan to'ldirish.
 *
 * ⚠️ «Depozitdan ko'p» holati TO'SILMAYDI, ogohlantiriladi. Qaror
 * serverniki (`ADR-024`): qarz limiti, agentning holati va oylik
 * chegara faqat u yerda ma'lum, panel esa ularning barchasini bilmaydi.
 * Tugmani o'chirib qo'yish kassirga "bu mumkin emas" degan YOLG'ON
 * ayta olardi.
 *
 * ⚠️ BITTA TO'LOV SHIFTI (`PER_TXN`) esa TO'SADI va bu ziddiyat emas:
 * u serverdan kelgan QAT'IY chegara va uni qarz limiti ham, agentning
 * holati ham ochmaydi — ya'ni "bu mumkin emas" bu yerda taxmin emas,
 * javobning o'zi. Chegara `fund-limits` dan olinadi, kodga yozilmaydi.
 */
export function FundPanel({
  subscriber,
  agentId,
  available,
  perTxnLimit,
  suggestions,
  currency,
  onBack,
}: FundPanelProps) {
  const { t } = useTranslation()
  const [amount, setAmount] = useState<Money | null>(null)
  const [armed, setArmed] = useState(false)
  const op = useCashOperation()

  const positive = amount !== null && amount.amount > 0
  /** Ikkalasi bir valyutada bo'lgandagina taqqoslanadi — konvertatsiya panelda YO'Q */
  const comparable =
    amount !== null && amount.amount > 0 && available !== null && available.currency === amount.currency
      ? { amount, available }
      : null
  const overDeposit = comparable !== null && comparable.amount.amount > comparable.available.amount
  // ⚠️ Valyutalar mos kelmasa TAQQOSLANMAYDI: konvertatsiya panelda yo'q
  // va ikki valyutani solishtirish yolg'on ogohlantirish berardi.
  const overPerTxn =
    amount !== null &&
    perTxnLimit !== null &&
    perTxnLimit.currency === amount.currency &&
    amount.amount > perTxnLimit.amount
  const remaining = comparable
    ? addMoney(comparable.available, negateMoney(comparable.amount))
    : null

  const submit = (idempotencyKey: string) => {
    if (amount === null || amount.amount <= 0) return
    const value = amount
    void op
      .run(async () => {
        const result = await fundSubscriber(agentId, subscriber.id, value, idempotencyKey)
        if (result.status === 'OK') return { tone: 'success' as const, message: result.message }
        if (result.status === 'PARTIAL') return { tone: 'partial' as const, message: result.message }
        return {
          tone: 'refused' as const,
          message: result.message,
          hint: t('cashdesk.fund.depositHint', { amount: formatMoneyValue(available) }),
        }
      })
      .then((outcome) => {
        // ⚠️ HAR NATIJADA tasdiq bosqichi yopiladi. Muvaffaqiyatda tugma
        // ekranda qolsa u ikkinchi to'lovga taklif bo'lib ko'rinardi; rad
        // javobida esa kassirga summani TUZATISH kerak, tugmani qayta
        // bosish emas — u aynan shunday rad etilardi.
        setArmed(false)
        if (outcome.tone === 'success' || outcome.tone === 'partial') setAmount(null)
      })
  }

  return (
    <Flex vertical gap={12}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack} style={{ alignSelf: 'flex-start' }}>
        {t('common.back')}
      </Button>

      <CashFeedbackAlert feedback={op.feedback} onClose={op.reset} />

      {suggestions.length > 0 ? (
        <Flex gap={8} wrap>
          {suggestions.map((value) => (
            <Button
              key={value.amount}
              size="large"
              onClick={() => setAmount(value)}
              style={{ height: 44, flex: '1 1 96px' }}
            >
              {formatMoneyValue(value)}
            </Button>
          ))}
        </Flex>
      ) : null}

      <div>
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {t('cashdesk.fund.amountLabel')}
        </Typography.Text>
        <MoneyInput
          value={amount}
          onChange={setAmount}
          currency={currency}
          size="large"
          disabled={op.running}
        />
      </div>

      {overDeposit ? (
        <Typography.Text type="warning" style={{ fontSize: 13 }}>
          {t('cashdesk.fund.overDeposit', { amount: formatMoneyValue(available) })}
        </Typography.Text>
      ) : null}

      {/* Shift OSHIB KETGANDA sabab yuborishdan OLDIN aytiladi: server
          bu so'rovni har holda rad etadi va rad javobi abonent
          qarshisida keladi. */}
      {overPerTxn ? (
        <Typography.Text type="danger" style={{ fontSize: 13 }}>
          {t('cashdesk.fund.overPerTxn', { amount: formatMoneyValue(perTxnLimit) })}
        </Typography.Text>
      ) : null}

      <ConfirmAction
        label={t('cashdesk.fund.action')}
        confirmLabel={t('cashdesk.fund.confirm', { amount: amount ? formatMoneyValue(amount) : '' })}
        disabled={!positive || overPerTxn || op.running}
        running={op.running}
        armed={armed}
        onArm={() => setArmed(true)}
        onCancel={() => setArmed(false)}
        onConfirm={submit}
        keyPrefix="kassa-fund"
        seed={`${subscriber.id}:${amount?.amount ?? 0}:${amount?.currency ?? currency}`}
        summary={
          <>
            <Typography.Text strong style={{ fontSize: 15, display: 'block' }}>
              {t('cashdesk.fund.summary', {
                amount: formatMoneyValue(amount),
                login: subscriber.login,
              })}
            </Typography.Text>
            {remaining ? (
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                {t('cashdesk.fund.afterDeposit', { amount: formatMoneyValue(remaining) })}
              </Typography.Text>
            ) : null}
          </>
        }
      />
    </Flex>
  )
}
